/**
 * ConnectorRequestPrompt
 *
 * Inline chat card (location-card style) that asks the user to connect
 * GitHub App, Kiro/Codex/Claude CLI credentials, or a Space repo link so
 * Losi Code can dispatch large coding runs. On success it emits
 * losi:connector-connected so useChatHandler auto-continues the turn.
 */

'use client'

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  Check,
  Github,
  GitBranch,
  KeyRound,
  Loader2,
  Plug,
  Terminal
} from 'lucide-react'
import { ChatbotUIContext } from '@/context/context'
import { ChatbotUIChatContext } from '@/context/chat'
import {
  emitConnectorConnected,
  type ConnectorConnectedEventDetail
} from '@/lib/connectors/events'
import {
  LOSI_CONNECTOR_LABELS,
  type LosiConnectorType
} from '@/lib/connectors/types'
import { ConnectSpaceDialog } from '@/components/spaces/connect/connect-space-dialog'
import { cn } from '@/lib/utils'

// --------------

export type ConnectorRequestItem = {
  type: LosiConnectorType
  connected: boolean
  label: string
  reason?: string
}

type PromptState = 'idle' | 'working' | 'success' | 'error'

type DeviceCodeResponse = {
  user_code: string
  verification_uri: string
  device_code: string
  interval: number
  expires_in: number
  instructions: string
}

// --------------

function connectorIcon(type: LosiConnectorType) {
  switch (type) {
    case 'github_app':
      return Github
    case 'repo_link':
      return GitBranch
    case 'kiro_cli':
    case 'codex_cli':
    case 'claude_code':
      return Terminal
    default:
      return Plug
  }
}

// --------------

async function readError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: unknown }
    if (typeof body.error === 'string' && body.error.trim()) return body.error
  } catch {
    // fall through
  }
  return response.statusText || 'Request failed'
}

// --------------

export function ConnectorRequestPrompt({
  reason,
  connectors,
  workspaceId,
  spaceId,
  onDeclined
}: {
  reason: string
  connectors: ConnectorRequestItem[]
  workspaceId?: string
  spaceId?: string
  onDeclined: () => void
}) {
  const { selectedWorkspace } = useContext(ChatbotUIContext)
  const { selectedChat } = useContext(ChatbotUIChatContext)

  const resolvedWorkspaceId = workspaceId || selectedWorkspace?.id || ''
  const chatId = selectedChat?.id || ''

  const pending = useMemo(
    () => connectors.filter((c) => !c.connected),
    [connectors]
  )

  const [activeType, setActiveType] = useState<LosiConnectorType | null>(
    pending[0]?.type ?? null
  )
  const [state, setState] = useState<PromptState>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [dismissed, setDismissed] = useState(false)
  const [deviceCode, setDeviceCode] = useState<DeviceCodeResponse | null>(null)
  const [apiKey, setApiKey] = useState('')
  const [repoDialogOpen, setRepoDialogOpen] = useState(false)
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (state === 'success') {
      const timer = setTimeout(() => setDismissed(true), 1600)
      return () => clearTimeout(timer)
    }
  }, [state])

  useEffect(() => {
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current)
    }
  }, [])

  // --------------

  const finish = useCallback(
    (detail: Omit<ConnectorConnectedEventDetail, 'chatId'>) => {
      if (!chatId) return
      setState('success')
      emitConnectorConnected({ ...detail, chatId })
    },
    [chatId]
  )

  // --------------

  const connectGithub = useCallback(() => {
    if (!resolvedWorkspaceId || !chatId) {
      setState('error')
      setErrorMessage('Missing workspace or chat context.')
      return
    }

    setState('working')
    const returnPath = `/chat/${chatId}`
    const url =
      `/api/integrations/github-app/install` +
      `?workspace_id=${encodeURIComponent(resolvedWorkspaceId)}` +
      `&redirect_uri=${encodeURIComponent(returnPath)}`
    window.location.assign(url)
  }, [chatId, resolvedWorkspaceId])

  // --------------

  const pollDeviceCode = useCallback(
    async (provider: LosiConnectorType, code: DeviceCodeResponse) => {
      const poll = async () => {
        try {
          const response = await fetch('/api/integrations/cli-providers/connect', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              workspace_id: resolvedWorkspaceId,
              provider,
              method: 'poll',
              device_code: code.device_code
            })
          })

          if (!response.ok) {
            throw new Error(await readError(response))
          }

          const data = (await response.json()) as {
            connected?: boolean
            pending?: boolean
            error?: string
          }

          if (data.connected) {
            finish({
              connector: provider,
              status: 'connected',
              label: LOSI_CONNECTOR_LABELS[provider]
            })
            return
          }

          if (data.error) {
            setState('error')
            setErrorMessage(data.error)
            return
          }

          pollTimer.current = setTimeout(poll, Math.max(2, code.interval || 5) * 1000)
        } catch (error) {
          setState('error')
          setErrorMessage(
            error instanceof Error ? error.message : 'Device-code poll failed'
          )
        }
      }

      pollTimer.current = setTimeout(poll, Math.max(2, code.interval || 5) * 1000)
    },
    [finish, resolvedWorkspaceId]
  )

  // --------------

  const startCliDeviceCode = useCallback(
    async (provider: LosiConnectorType) => {
      if (!resolvedWorkspaceId) {
        setState('error')
        setErrorMessage('Missing workspace context.')
        return
      }

      setState('working')
      setErrorMessage('')
      setDeviceCode(null)

      try {
        const response = await fetch('/api/integrations/cli-providers/connect', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workspace_id: resolvedWorkspaceId,
            provider,
            method: 'device_code'
          })
        })

        if (!response.ok) {
          throw new Error(await readError(response))
        }

        const data = (await response.json()) as DeviceCodeResponse
        setDeviceCode(data)
        setState('idle')
        void pollDeviceCode(provider, data)
      } catch (error) {
        setState('error')
        setErrorMessage(
          error instanceof Error ? error.message : 'Failed to start device code'
        )
      }
    },
    [pollDeviceCode, resolvedWorkspaceId]
  )

  // --------------

  const submitCliApiKey = useCallback(
    async (provider: LosiConnectorType) => {
      if (!resolvedWorkspaceId || !apiKey.trim()) {
        setState('error')
        setErrorMessage('Enter an API key first.')
        return
      }

      setState('working')
      setErrorMessage('')

      try {
        const response = await fetch('/api/integrations/cli-providers/connect', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workspace_id: resolvedWorkspaceId,
            provider,
            method: 'api_key',
            api_key: apiKey.trim()
          })
        })

        if (!response.ok) {
          throw new Error(await readError(response))
        }

        finish({
          connector: provider,
          status: 'connected',
          label: LOSI_CONNECTOR_LABELS[provider]
        })
      } catch (error) {
        setState('error')
        setErrorMessage(
          error instanceof Error ? error.message : 'Failed to save API key'
        )
      }
    },
    [apiKey, finish, resolvedWorkspaceId]
  )

  // --------------

  const handlePrimary = useCallback(
    (type: LosiConnectorType) => {
      setActiveType(type)
      if (type === 'github_app') {
        connectGithub()
        return
      }
      if (type === 'repo_link') {
        if (!spaceId || !resolvedWorkspaceId) {
          setState('error')
          setErrorMessage('A Space id is required to connect a repository.')
          return
        }
        setRepoDialogOpen(true)
        return
      }
      // CLI providers — prefer device code when supported; Claude is key-only.
      if (type === 'claude_code') {
        setDeviceCode(null)
        setState('idle')
        return
      }
      void startCliDeviceCode(type)
    },
    [connectGithub, resolvedWorkspaceId, spaceId, startCliDeviceCode]
  )

  // --------------

  if (dismissed) return null

  if (state === 'success') {
    return (
      <div className="border-border/40 bg-card/50 max-w-sm rounded-xl border p-4 backdrop-blur-sm">
        <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
          <Check className="size-4" />
          <span className="text-sm font-medium">Connector connected</span>
        </div>
      </div>
    )
  }

  const active = connectors.find((c) => c.type === activeType) || pending[0]

  return (
    <div className="border-border/40 bg-card/50 max-w-md rounded-xl border p-4 backdrop-blur-sm">
      <div className="flex items-center gap-2">
        <Plug className="text-primary size-4" />
        <span className="text-sm font-medium">Connector Request</span>
      </div>

      <p className="text-muted-foreground mt-2 text-sm">{reason}</p>

      <p className="text-muted-foreground/70 mt-2 text-[11px]">
        Connect what Losi needs for CLI coding runs, then you will return here and
        the chat continues automatically.
      </p>

      <div className="mt-3 space-y-2">
        {pending.map((item) => {
          const Icon = connectorIcon(item.type)
          const isActive = active?.type === item.type
          return (
            <button
              key={item.type}
              type="button"
              onClick={() => handlePrimary(item.type)}
              className={cn(
                'border-border/60 flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors',
                isActive ? 'border-primary/40 bg-primary/5' : 'hover:bg-muted/40'
              )}
            >
              <span className="bg-muted text-foreground flex size-8 shrink-0 items-center justify-center rounded-lg">
                <Icon className="size-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="text-foreground block text-sm font-medium">
                  {item.label}
                </span>
                {item.reason ? (
                  <span className="text-muted-foreground mt-0.5 block text-xs">
                    {item.reason}
                  </span>
                ) : null}
              </span>
            </button>
          )
        })}
      </div>

      {active &&
        (active.type === 'kiro_cli' ||
          active.type === 'codex_cli' ||
          active.type === 'claude_code') && (
          <div className="border-border/50 mt-3 space-y-2 rounded-lg border border-dashed p-3">
            {deviceCode ? (
              <>
                <p className="text-muted-foreground text-xs">
                  {deviceCode.instructions ||
                    `Open the verification link and enter code ${deviceCode.user_code}.`}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <code className="bg-muted rounded px-2 py-1 text-sm font-semibold tracking-wider">
                    {deviceCode.user_code}
                  </code>
                  <a
                    href={deviceCode.verification_uri}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary text-xs underline"
                  >
                    Open verification
                  </a>
                </div>
                <div className="text-muted-foreground flex items-center gap-2 text-xs">
                  <Loader2 className="size-3.5 animate-spin" />
                  Waiting for authorization...
                </div>
              </>
            ) : (
              <>
                <div className="text-muted-foreground flex items-center gap-2 text-xs">
                  <KeyRound className="size-3.5" />
                  Or paste an API key
                </div>
                <input
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={
                    active.type === 'kiro_cli'
                      ? 'ksk_...'
                      : active.type === 'claude_code'
                        ? 'sk-ant-...'
                        : 'sk-...'
                  }
                  className="border-border bg-background w-full rounded-md border px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={() => void submitCliApiKey(active.type)}
                  className="bg-primary text-primary-foreground rounded-lg px-3 py-1.5 text-sm font-medium"
                >
                  Save key
                </button>
              </>
            )}
          </div>
        )}

      {state === 'working' && (
        <div className="text-muted-foreground mt-3 flex items-center gap-2">
          <Loader2 className="size-3.5 animate-spin" />
          <span className="text-xs">Connecting...</span>
        </div>
      )}

      {state === 'error' && errorMessage ? (
        <p className="mt-3 text-xs text-red-500">{errorMessage}</p>
      ) : null}

      <div className="mt-3 flex gap-2">
        {active ? (
          <button
            type="button"
            onClick={() => handlePrimary(active.type)}
            className="bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-medium transition-opacity hover:opacity-90"
          >
            Connect {active.label}
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => {
            if (active) {
              finish({
                connector: active.type,
                status: 'declined',
                label: active.label
              })
            }
            onDeclined()
          }}
          className="bg-muted text-muted-foreground rounded-lg px-4 py-2 text-sm font-medium transition-opacity hover:opacity-80"
        >
          Decline
        </button>
      </div>

      {spaceId && resolvedWorkspaceId ? (
        <ConnectSpaceDialog
          open={repoDialogOpen}
          onOpenChange={(open) => {
            setRepoDialogOpen(open)
            if (open) return

            void (async () => {
              try {
                const res = await fetch(`/api/spaces/${spaceId}/repo-links`, {
                  cache: 'no-store'
                })
                if (!res.ok) return
                const data = (await res.json()) as { links?: unknown[] }
                if (Array.isArray(data.links) && data.links.length > 0) {
                  finish({
                    connector: 'repo_link',
                    status: 'connected',
                    label: 'Repository',
                    spaceId
                  })
                }
              } catch {
                // Ignore — Losi will re-check readiness on the next turn.
              }
            })()
          }}
          spaceId={spaceId}
          workspaceId={resolvedWorkspaceId}
        />
      ) : null}
    </div>
  )
}
